import { ApiService } from '../../../types/api';
import type { Theme, ThemeCreateRequest, ThemeRenameRequest } from '../types/theme';
import { ThemeEditorApi, type ThemeEditorApiContract } from './editor.api';

export interface ThemesApiContract {
  list(): Promise<Theme[]>;
  create(payload: ThemeCreateRequest): Promise<Theme>;
  rename(id: string, payload: ThemeRenameRequest): Promise<Theme>;
  publish(id: string): Promise<Theme>;
  remove(id: string): Promise<void>;
  downloadZip(id: string): Promise<ArrayBuffer>;
  uploadZip(id: string, base64: string): Promise<void>;
  editor(): ThemeEditorApiContract;
}

declare module '../../../context/page-context' {
  interface Apis {
    themes: ThemesApiContract;
  }
}

export class ThemesApi extends ApiService implements ThemesApiContract {
  public list(): Promise<Theme[]> {
    return this.api.proxy({
      method: 'GET',
      path: '/v1/themes',
    });
  }

  public create(payload: ThemeCreateRequest): Promise<Theme> {
    return this.api.proxy({
      method: 'POST',
      path: '/v1/themes',
      payload,
    });
  }

  public rename(id: string, payload: ThemeRenameRequest): Promise<Theme> {
    return this.api.proxy({
      method: 'PATCH',
      path: '/v1/themes/{id}',
      params: { id },
      payload,
    });
  }

  public publish(id: string): Promise<Theme> {
    return this.api.proxy({
      method: 'PATCH',
      path: '/v1/themes/{id}',
      params: { id },
      payload: { is_published: true },
    });
  }

  public remove(id: string): Promise<void> {
    return this.api.proxy({
      method: 'DELETE',
      path: '/v1/themes/{id}',
      params: { id },
    });
  }

  public downloadZip(id: string): Promise<ArrayBuffer> {
    return this.api.proxy({
      method: 'GET',
      path: '/v1/themes/{id}/zip',
      params: { id },
      contentType: 'application/zip',
      responseType: 'arraybuffer',
    });
  }

  public uploadZip(id: string, base64: string): Promise<void> {
    return this.api.proxy({
      method: 'POST',
      path: '/v1/themes/{id}/zip',
      params: { id },
      payload: base64,
      contentType: 'application/zip',
    });
  }

  public editor(): ThemeEditorApiContract {
    return new ThemeEditorApi(this.api);
  }
}
